// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
// import CardListHeading from "@insite/content-library/Components/CardListHeading";
// import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import DataTable, { DataTableProps } from "@rpsc/MobiusOverrides/DataTable/DataTable";
import DataTableHeader, { DataTableHeaderProps } from "@rpsc/MobiusOverrides/DataTable/DataTableHeader";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@rpsc/Overrides/Components/QuoteSummaryCard";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// <xng-change>
const enum fields {
    numberOfRecords = "numberOfRecords",
    hideHeaderTitle = "hideHeaderTitle",
    viewAllLinkLabel = "viewAllLinkLabel",
    showQuoteNumber = "showQuoteNumber",
    showType = "showType",
    showStatus = "showStatus",
    showRequested = "showRequested",
    showShipTo = "showShipTo",
    showExpires = "showExpires",
    showSalesReq = "showSalesReq",
    showUser = "showUser",
    showCustomer = "showCustomer",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.hideHeaderTitle]: boolean;
        [fields.viewAllLinkLabel]: string;
        [fields.showQuoteNumber]: boolean;
        [fields.showType]: boolean;
        [fields.showStatus]: boolean;
        [fields.showRequested]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showExpires]: boolean;
        [fields.showSalesReq]: boolean;
        [fields.showUser]: boolean;
        [fields.showCustomer]: boolean;
    };
}
// </xng-change>

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    // XNG-Change
    recentQuotesParameter.pageSize = props.fields.numberOfRecords || recentQuotesParameter.pageSize;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Change
        tabs: (state as CustomAppState).xTabs,
    };
};

const mapDispatchToProps = {
    loadQuotes,
    updateZone: (zone: string, enabled: boolean) => ({
        type: "xTabs/EnableZone",
        data: {
            zone,
            isEnabled: enabled, // we need to show this tab if we are in tab context
        },
    }),
};

// xng-change
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // xng-change
    container?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            // XNG-Changes
            // recentQuotesParameter.pageSize = this.props.fields.numberOfRecords;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    // XNG-change
    componentDidUpdate(): void {
        if (this.props.zone.includes("tab")) {
            if (this.props.canViewQuotes && this.props.quotesDataView.value && !this.props.tabs[this.props.zone]) {
                this.props.updateZone(this.props.zone, true);
            } else if (!this.props.canViewQuotes || !this.props.quotesDataView.value) {
                this.props.updateZone(this.props.zone, false);
            }
        }
    }

    render() {
        const {
            settingsCollection,
            session,
            quotesDataView,
            myQuotesPageNavLink,
            canViewQuotes,
            // xng-change
            fields: {
                hideHeaderTitle,
                showCustomer,
                showExpires,
                showQuoteNumber,
                showRequested,
                showSalesReq,
                showShipTo,
                showStatus,
                showType,
                showUser,
                viewAllLinkLabel,
            },
        } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        // xng-change
        const cmsProperties = {
            viewAllLinkLabel,
            showQuoteNumber,
            showType,
            showStatus,
            showRequested,
            showExpires,
            showShipTo,
            showSalesReq,
            showUser,
            showCustomer,
        };

        // xng-change: change the structure
        return (
            // <CardList
            //     css={css`
            //         padding-bottom: 50px;
            //     `}
            //     extendedStyles={styles.cardList}
            // >
            //     <CardListHeading heading={translate("Recent Quotes")} viewAllUrl={myQuotesPageUrl} />
            //     {quotesDataView.value.length === 0 && (
            //         <GridItem {...styles.noQuotesGridItem}>
            //             <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
            //         </GridItem>
            //     )}
            //     {quotesDataView.value.map(quote => (
            //         <CardContainer key={quote.id} extendedStyles={styles.cardContainer}>
            //             <QuoteSummaryCard
            //                 quote={quote}
            //                 session={session}
            //                 quoteSettings={settingsCollection.quoteSettings}
            //                 extendedStyles={styles.quoteSummaryCard}
            //             />
            //         </CardContainer>
            //     ))}
            // </CardList>

            <StyledWrapper {...styles.container}>
                {!hideHeaderTitle &&
                    createWidgetElement("XTitledSection/XTitledSection", {
                        fields: {
                            title: translate("Recent Quotes"),
                            variant: "h3",
                            buttonTitle: translate(viewAllLinkLabel),
                            buttonLink: myQuotesPageUrl,
                            addZone: false,
                            margin: "0 0 16px 0",
                        },
                    })}

                {quotesDataView.value.length === 0 && (
                    <GridItem {...styles.noQuotesGridItem}>
                        <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                    </GridItem>
                )}

                {quotesDataView.value.length > 0 && (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            {cmsProperties.showQuoteNumber && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Quote #")}</DataTableHeader>
                            )}
                            {cmsProperties.showType && settingsCollection.quoteSettings.jobQuoteEnabled && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Type")}</DataTableHeader>
                            )}
                            {cmsProperties.showStatus && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Status")}</DataTableHeader>
                            )}
                            {cmsProperties.showRequested && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Requested")}</DataTableHeader>
                            )}
                            {cmsProperties.showExpires && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Expires")}</DataTableHeader>
                            )}
                            {cmsProperties.showSalesReq && session.isSalesPerson && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Sales Req")}</DataTableHeader>
                            )}
                            {/* XNG-Change */}
                            {cmsProperties.showUser && session.isSalesPerson && (
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Ordered By")}
                                </DataTableHeader>
                            )}
                            {cmsProperties.showCustomer && (
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Customer")}</DataTableHeader>
                            )}
                            {cmsProperties.showShipTo && !session.isSalesPerson && (
                                <DataTableHeader tight {...styles.dataTableHeaders}>
                                    {translate("Ship To")}
                                </DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {quotesDataView.value.map(quote => (
                                <QuoteSummaryCard
                                    key={quote.id}
                                    quote={quote}
                                    session={session}
                                    quoteSettings={settingsCollection.quoteSettings}
                                    extendedStyles={styles.quoteSummaryCard}
                                    cmsProperties={cmsProperties}
                                />
                            ))}
                        </DataTableBody>
                    </DataTable>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        // xng-change
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.hideHeaderTitle,
                displayName: "Hide Header Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.viewAllLinkLabel,
                fieldType: "General",
                defaultValue: "",
                editorTemplate: "TextField",
                isVisible: item => !item.fields.hideHeaderTitle,
                sortOrder: 2,
            },
            {
                name: fields.showQuoteNumber,
                displayName: "Quote #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showType,
                displayName: "Type",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showRequested,
                displayName: "Requested",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showExpires,
                displayName: "Expires",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showSalesReq,
                displayName: "Sales Req",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showUser,
                displayName: "Ordered By",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showCustomer,
                displayName: "Customer",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 11,
            },
        ],
    },
};

export default widgetModule;
